import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ArchiveRestore, ArrowUpRight, Archive, Briefcase, UserCircle2 } from "lucide-react";

export default function ArchivedProjects({ search = "" }) {
  const [projects, setProjects] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/projects?archived=true");
      setProjects(data);
    } catch { setProjects([]); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (p) => {
    try {
      await api.post(`/projects/${p.id}/unarchive`);
      toast.success(`"${p.name}" restored to your active projects`);
      load();
    } catch { toast.error("Could not restore project"); }
  };

  if (projects === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const shown = search.trim()
    ? projects.filter((p) => `${p.name} ${p.industry || ""} ${p.description || ""}`.toLowerCase().includes(search.trim().toLowerCase()))
    : projects;
  if (projects.length === 0) return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="archive-empty">
      <Archive className="h-8 w-8 text-primary mx-auto mb-3" />
      <p className="text-muted-foreground">No archived projects. When a project wraps up, archive it from the Projects page to keep your workspace tidy.</p>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archive-grid">
      {shown.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No archived projects match "{search}".</p>}
      {shown.map((p) => {
        const pct = p.task_count ? Math.round((p.done_count / p.task_count) * 100) : 0;
        return (
          <div key={p.id} className="border border-border rounded-lg p-5 bg-card" data-testid={`archived-project-${p.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Archive className="h-4 w-4 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Archived {p.archived_at ? new Date(p.archived_at).toLocaleDateString() : ""}
              </span>
            </div>
            <h3 className="font-display font-bold tracking-tight truncate">{p.name}</h3>
            <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
              {p.owner_name && <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground/75" title="Project lead" data-testid={`archived-lead-${p.id}`}><UserCircle2 className="h-3.5 w-3.5 text-primary" /> {p.owner_name}</span>}
              {p.industry && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Briefcase className="h-3 w-3" /> {p.industry}</span>}
            </span>
            <p className="text-xs text-muted-foreground mt-2">{p.done_count}/{p.task_count} tasks done · {pct}% complete</p>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => restore(p)} data-testid={`restore-project-${p.id}`}>
                <ArchiveRestore className="h-3.5 w-3.5" /> Restore
              </Button>
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => nav(`/projects/${p.id}`)} data-testid={`open-archived-${p.id}`}>
                View <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
